// web/src/player/smartZoom.ts
import type { DetectedObject, WordTiming, ZoomResult } from "./types";
import { containRect } from "./geometry";

export const ZOOM = {
  ANTICIPATION_MS: 500, LINGERING_MS: 2000,
  DEFAULT:    { MIN_SCALE: 1.2, MAX_SCALE: 3.0,  PADDING: 1.2 },
  TALL_IMAGE: { MIN_SCALE: 4.0, MAX_SCALE: 12.0, PADDING: 1.05 },
  DECK:       { MIN_SCALE: 1.5, MAX_SCALE: 7.0,  PADDING: 1.05 },
  DOC:        { MIN_SCALE: 2.0, MAX_SCALE: 12.0, PADDING: 1.05 },
  TALL_IMAGE_THRESHOLD: 0.67,
  SMALL_OBJECT_AREA: 0.05,
  SMALL_OBJECT_PADDING: 1.02,
} as const;

// ---------------------------------------------------------------------------
// Normalization helpers (iOS findDialogueTiming port)
// ---------------------------------------------------------------------------

/** Normalize a single word from the timing list for comparison. */
function normalizeTimingWord(w: string): string {
  return w
    .toLowerCase()
    .replace(/\[.*?\]/g, "")   // strip audio-tag spans
    .replace(/[.,!?'"]/g, "")  // strip punctuation
    .replace(/[\[\]]/g, "")    // strip stray brackets
    .trim();
}

/**
 * Normalize a dialogue_reference string and split into an array of words.
 * Removes [audio tags], markdown **, punctuation, stray brackets.
 */
function normalizeRef(ref: string): string[] {
  const cleaned = ref
    .toLowerCase()
    .replace(/\[.*?\]/g, " ")  // replace audio-tag spans with space
    .replace(/[.,!?'"]/g, "")  // strip punctuation
    .replace(/\*\*/g, "")      // strip markdown bold
    .replace(/[\[\]]/g, "")    // strip stray brackets
    .trim();
  return cleaned.split(/\s+/).filter((w) => w.length > 0);
}

/** True for timing words that are audio-tag artifacts and should be skipped. */
function isAudioTagWord(word: string): boolean {
  const t = word.trim();
  return t === "" || t.startsWith("[") || t.endsWith("]");
}

// ---------------------------------------------------------------------------
// Primary matching helper (exported for direct unit-testing if needed)
// ---------------------------------------------------------------------------

/**
 * Find the best timing span for `reference` inside `words`.
 *
 * Strategy order (matches iOS findDialogueTiming):
 *  1. Exact consecutive match of normalized reference words.
 *  2. Individual word: first timing word equal to ANY reference word.
 *  3. Un-hyphenated: split reference words on `-` and match sub-words.
 *  4. Substring (len > 4): timing word contains reference word, or vice-versa.
 *
 * Returns indices into the original `words` array (not the filtered array).
 * For single-word fallback results startIdx === endIdx.
 */
export function findRefTiming(
  reference: string,
  words: WordTiming[],
  opts: { exactOnly?: boolean } = {}
): { startIdx: number; endIdx: number } | null {
  const refWords = normalizeRef(reference);
  if (refWords.length === 0) return null;

  // Pre-filter: skip audio-tag / bracket fragments, keep original indices.
  const clean = words
    .map((w, idx) => ({ w, idx }))
    .filter(({ w }) => !isAudioTagWord(w.word));

  // Pre-compute normalized form of each clean timing word.
  const cleanNorm = clean.map(({ w, idx }) => ({
    norm: normalizeTimingWord(w.word),
    idx,
  }));

  const n = cleanNorm.length;
  const r = refWords.length;

  // ---- Strategy 1: exact consecutive match --------------------------------
  for (let i = 0; i <= n - r; i++) {
    let match = true;
    for (let j = 0; j < r; j++) {
      if (cleanNorm[i + j].norm !== refWords[j]) { match = false; break; }
    }
    if (match) {
      return { startIdx: cleanNorm[i].idx, endIdx: cleanNorm[i + r - 1].idx };
    }
  }

  if (opts.exactOnly) return null; // cue must be a verbatim substring; no fuzzy fallback

  // ---- Strategy 2: individual word ----------------------------------------
  for (const refWord of refWords) {
    for (const { w, idx } of clean) {
      if (normalizeTimingWord(w.word) === refWord) {
        return { startIdx: idx, endIdx: idx };
      }
    }
  }

  // ---- Strategy 3: un-hyphenated ------------------------------------------
  for (const refWord of refWords) {
    const parts = refWord.split("-");
    if (parts.length > 1) {
      for (const part of parts) {
        for (const { w, idx } of clean) {
          if (normalizeTimingWord(w.word) === part) {
            return { startIdx: idx, endIdx: idx };
          }
        }
      }
    }
  }

  // ---- Strategy 4: substring (reference word length > 4) ------------------
  for (const refWord of refWords) {
    if (refWord.length > 4) {
      for (const { w, idx } of clean) {
        const tw = normalizeTimingWord(w.word);
        if (tw.length > 0 && (tw.includes(refWord) || refWord.includes(tw))) {
          return { startIdx: idx, endIdx: idx };
        }
      }
    }
  }

  return null;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export function buildZoomWindows(objects: DetectedObject[], words: WordTiming[]) {
  return objects.map((obj) => {
    const match = findRefTiming(obj.dialogue_reference, words);
    if (!match) return null;
    return {
      obj,
      startSec: words[match.startIdx].start - ZOOM.ANTICIPATION_MS / 1000,
      endSec:   words[match.endIdx].end   + ZOOM.LINGERING_MS   / 1000,
    };
  }).filter(Boolean) as { obj: DetectedObject; startSec: number; endSec: number }[];
}

export function computeZoom(
  time: number,
  windows: ReturnType<typeof buildZoomWindows>,
  vpW: number, vpH: number, imgNatW: number, imgNatH: number,
  profile: "auto" | "deck" | "doc" = "auto",
): ZoomResult | null {
  const w = windows.find((x) => time >= x.startSec && time <= x.endSec);
  if (!w || !imgNatW || !imgNatH) return null;

  const [ymin, xmin, ymax, xmax] = w.obj.box_2d;            // 0-1000, [ymin,xmin,ymax,xmax]
  const sx = imgNatW / 1000, sy = imgNatH / 1000;
  const bxMinN = xmin * sx, byMinN = ymin * sy, bxMaxN = xmax * sx, byMaxN = ymax * sy;
  const bwN = bxMaxN - bxMinN, bhN = byMaxN - byMinN;
  if (bwN <= 0 || bhN <= 0) return null;
  const cxN = bxMinN + bwN / 2, cyN = byMinN + bhN / 2;

  const imgAR = imgNatW / imgNatH;
  const isTall = profile === "doc" || (profile === "auto" && imgAR < ZOOM.TALL_IMAGE_THRESHOLD);
  const cfg = profile === "deck" ? ZOOM.DECK : profile === "doc" ? ZOOM.DOC : (isTall ? ZOOM.TALL_IMAGE : ZOOM.DEFAULT);

  const { offX, offY, dispW } = containRect(vpW, vpH, imgNatW, imgNatH);

  const toDisp = dispW / imgNatW;
  const bwD = bwN * toDisp, bhD = bhN * toDisp, cxD = cxN * toDisp, cyD = cyN * toDisp;

  const areaRatio = (bwN * bhN) / (imgNatW * imgNatH);
  const isSmall = areaRatio < ZOOM.SMALL_OBJECT_AREA;
  const padW = isSmall ? bwD * ZOOM.SMALL_OBJECT_PADDING : bwD * cfg.PADDING;
  const padH = isSmall ? bhD * ZOOM.SMALL_OBJECT_PADDING : bhD * cfg.PADDING;

  let scale = isTall ? vpW / padW : Math.min(vpW / padW, vpH / padH);
  scale = Math.min(Math.max(scale, cfg.MIN_SCALE), cfg.MAX_SCALE);

  const vpCx = vpW / 2, vpCy = vpH / 2;
  const cxVp = offX + cxD, cyVp = offY + cyD;
  const tx = -(cxVp - vpCx) * scale;
  let ty = -(cyVp - vpCy) * scale;

  if (isTall && !isSmall) {
    const topVp = offY + (cyD - bhD / 2);
    ty = vpH * 0.1 - (vpCy + (topVp - vpCy) * scale);
  }
  return { isActive: true, transform: `translate3d(${tx}px, ${ty}px, 0) scale(${scale})`,
           transformOrigin: "center center", scale };
}
